import DTO from '@/dataAccess/dto/DTO'
import {
  COMMUNITY_LIVING_SERVICE_ABBREVIATION,
  PRIORITY_MAPPER
} from '@/constants'
import store from '@/dataAccess/store'
import API from '@/dataAccess/api'
import Service from '@/dataAccess/models/Service'

export default class CommunityLivingClient extends DTO {
  static get url() {
    return `clients-currently-on-service/${COMMUNITY_LIVING_SERVICE_ABBREVIATION}/`
  }

  readonly id: number
  readonly fullName: string
  scheduleAccomplished: ScheduleAccomplishmentStatus = null
  daySummary = ''
  validationErrors: string[] = []

  constructor({ id, full_name }) {
    super()
    this.id = id
    this.fullName = full_name
  }

  /**
   * Validate whether the current state is suitable for forming and Action
   * (no data is also OK, but partial date is not).
   * If errors exist, that are stored in `validationErrors`.
   *
   * @return whether the validation passed or not.
   */
  validate(): boolean {
    this.validationErrors = [] // remove old errors
    if (this.daySummary && this.scheduleAccomplished === null) {
      this.validationErrors.push(
        'Kui on lisatud kirjeldus, tuleb ka märkida, kas päevaplaan on täidetud.'
      )
    }
    if (
      this.scheduleAccomplished === ScheduleAccomplishmentStatus.NO &&
      !this.daySummary
    ) {
      this.validationErrors.push(
        'Kui klient ei ole päevaplaani täitnud, tuleb lisada selgitus.'
      )
    }
    return this.validationErrors.length === 0
  }

  /**
   * Indicate whether data for constructing an Action
   * has been entered about this client.
   */
  isDataEntered(): boolean {
    return this.scheduleAccomplished !== null
  }

  /**
   * Return object which can be posted to API to create a new Action.
   *
   * @remarks
   * ## Action priority
   *
   * If the client accomplished their daily schedule, the priority is low,
   * if some additional info was provided, the priority is raised to medium
   * and if the client did not accomplished their daily schedule,
   * there is a problem and thus the priority is high.
   *
   * ## Description
   *
   * '🌻 Päevaplaan täidetud 🌻' is the default description. If a custom message can override it.
   * When the daily schedule was not filled, a custom description is mandatory.
   */
  compileAction(date: string) {
    let priority = PRIORITY_MAPPER.LOW
    if (this.daySummary) priority = PRIORITY_MAPPER.MEDIUM
    if (this.scheduleAccomplished === ScheduleAccomplishmentStatus.NO)
      priority = PRIORITY_MAPPER.HIGH

    return {
      employees: [store.getters.user.id],
      start: date + 'T00:00',
      is_timed: false,
      description: this.daySummary
        ? this.daySummary
        : '🌻 Päevaplaan täidetud 🌻',
      clients: [this.id],
      priority: priority,
      service: CommunityLivingClient.getCommunityLivingService.id
    }
  }

  private static get getCommunityLivingService() {
    return Service.query()
      .where('abbreviation', COMMUNITY_LIVING_SERVICE_ABBREVIATION)
      .get()[0]
  }

  static async postActions(clients: CommunityLivingClient[], date: string) {
    if (!clients.every(client => client.validate())) {
      throw Error(
        'Validation errors occurred, they are visible in the `validationErrors` property'
      )
    }
    const actions = clients.map(d => d.compileAction(date))
    await API.postMultipleActions(actions)
  }
}

export enum ScheduleAccomplishmentStatus {
  YES,
  NO
}
